import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function VerAfiliadoAdmin() {
  const { id } = useParams<{ id: string }>();
  const [afiliado, setAfiliado] = useState<any>(null);
  const [inmobiliariaNombre, setInmobiliariaNombre] = useState<string>('Sin asignar');
  const [inmobiliariaId, setInmobiliariaId] = useState<string>('');
  const [propiedades, setPropiedades] = useState<any[]>([]);
  const [reseñas, setReseñas] = useState<any[]>([]);
  const [creditos, setCreditos] = useState(0);
  const [talleres, setTalleres] = useState<any[]>([]);
  const [talleresCompletados, setTalleresCompletados] = useState(0);
  const [loading, setLoading] = useState(true);
  const [mostrarCambiarAgencia, setMostrarCambiarAgencia] = useState(false);
  const [agenciasDisponibles, setAgenciasDisponibles] = useState<any[]>([]);
  const [busquedaAgencia, setBusquedaAgencia] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        // Obtener datos del afiliado
        const docRef = doc(db, 'asesor', id);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
          console.error('Afiliado no encontrado:', id);
          setLoading(false);
          return;
        }
        const data = docSnap.data();
        setAfiliado({ id: docSnap.id, ...data });

        // ✅ CORREGIDO: Buscar inmobiliaria usando doc.id
        if (data.inmobiliaria_doc_ids && Array.isArray(data.inmobiliaria_doc_ids) && data.inmobiliaria_doc_ids.length > 0) {
          try {
            const inmobiliariaDocId = data.inmobiliaria_doc_ids[0];

            const inmoDocRef = doc(db, 'inmobiliaria', inmobiliariaDocId);
            const inmoSnap = await getDoc(inmoDocRef);

            if (inmoSnap.exists()) {
              setInmobiliariaNombre(inmoSnap.data().nombre || 'Sin nombre');
              setInmobiliariaId(inmobiliariaDocId);
            } else {
              setInmobiliariaNombre('Inmobiliaria no encontrada');
            }
          } catch (error) {
            console.error('Error buscando inmobiliaria:', error);
            setInmobiliariaNombre('Error al cargar inmobiliaria');
          }
        }

        // Propiedades MLS activas del agente
        if (docSnap.id) {
          try {
            const propsQuery = query(
              collection(db, 'propiedades'),
              where('asesor_doc_ids', 'array-contains', docSnap.id),
              where('fuente', '==', 'flexmls'),
              where('estado', '==', 'A')
            );
            const snapProps = await getDocs(propsQuery);
            const props = snapProps.docs.map(p => ({ id: p.id, ...(p.data() as any) }));
            setPropiedades(props);
          } catch (error) {
            console.error('Error cargando propiedades:', error);
            setPropiedades([]);
          }
        } else {
          setPropiedades([]);
        }

        // Buscar reseñas
        if (data.asesor_id) {
          try {
            const calQuery = query(
              collection(db, 'calificacion'), 
              where('perfil_id', '==', data.asesor_id), 
              where('estatus', '==', 'aprobada')
            );
            const calSnap = await getDocs(calQuery);
            const calData = calSnap.docs.map(d => d.data());
            setReseñas(calData);
          } catch (error) {
            console.error('Error cargando reseñas:', error);
            setReseñas([]);
          }
        }

        // Buscar talleres e inscripciones usando doc.id (siguiendo el enfoque de dashboard)
        const perfilDocId = docSnap.id;
        if (perfilDocId) {
          try {
            if (process.env.NODE_ENV === "development") console.log('🔍 Buscando inscripciones para perfil doc.id:', perfilDocId);
            const inscQuery = query(
              collection(db, 'inscripcion'),
              where('perfil_id', '==', perfilDocId)
            );
            const snapInscripcion = await getDocs(inscQuery);
            let totalCreditos = 0;

            const talleresData = await Promise.all(
              snapInscripcion.docs.map(async (docSnap) => {
                const inscripcion = docSnap.data();
                const inscripcionId = docSnap.id;

                // ✅ Usar taller_doc_id (doc.id de Firestore) en lugar de taller_id (legacy)
                const tallerDocId = inscripcion.taller_doc_id || inscripcion.taller_id;
                if (!tallerDocId) {
                  if (process.env.NODE_ENV === "development") console.warn('Inscripción sin taller_doc_id:', inscripcionId);
                  return null;
                }

                const tallerRef = doc(db, 'taller', tallerDocId);
                const tallerSnap = await getDoc(tallerRef);
                const tallerInfo = tallerSnap.exists() ? tallerSnap.data() : null;

                let creditos = 0;
                let estatus = 'Sin empezar';

                if (inscripcion.completado) {
                  estatus = 'Listo';
                  if (typeof inscripcion.creditos === 'number') {
                    creditos = inscripcion.creditos;
                  } else if (tallerInfo && typeof tallerInfo.creditos === 'number') {
                    creditos = tallerInfo.creditos;
                    try {
                      await updateDoc(doc(db, 'inscripcion', inscripcionId), { creditos });
                    } catch (err) {
                      if (process.env.NODE_ENV === "development") console.warn('No se pudo actualizar inscripción:', err);
                    }
                  }
                  totalCreditos += creditos;
                } else if (inscripcion.progreso) {
                  estatus = 'En progreso';
                }

                return tallerInfo
                  ? {
                      ...tallerInfo,
                      estatus
                    }
                  : null;
              })
            );

            const talleresValidos = talleresData.filter(Boolean);
            setTalleres(talleresValidos);

            // Contar solo talleres completados para "Talleres Asistidos" (siguiendo el enfoque de dashboard)
            const completados = talleresValidos.filter((t: any) => t && t.estatus === 'Listo');
            setTalleresCompletados(completados.length);

            if (process.env.NODE_ENV === "development") console.log(`📊 Talleres totales: ${talleresValidos.length}, Completados: ${completados.length}, Créditos: ${totalCreditos}`);

            setCreditos(totalCreditos);
          } catch (error) {
            console.error('Error cargando talleres:', error);
            setTalleres([]);
            setTalleresCompletados(0);
            setCreditos(0);
          }
        }
        
      } catch (error) {
        console.error('Error cargando datos del afiliado:', error);
        setInmobiliariaNombre('Error al cargar');
        setPropiedades([]);
        setReseñas([]);
        setTalleres([]);
        setTalleresCompletados(0);
        setCreditos(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Calcular promedio de calificación
  // Si hay reseñas aprobadas, usar el promedio de estrellas (1-5)
  // Si no hay reseñas pero existe calificacion en el perfil (0-100), convertir a escala 1-5
  const promedioCalificacion = reseñas.length > 0
    ? (reseñas.reduce((acc: number, r: any) => acc + (r.estrellas || 0), 0) / reseñas.length).toFixed(1)
    : afiliado?.calificacion
      ? (afiliado.calificacion / 20).toFixed(1)
      : 'N/A';

  const getBadge = (text: string, color: string) => (
    <span className={`badge bg-${color} ms-2`}>{text}</span>
  );

  const getColorEstatus = (estatus: string) => {
    switch (estatus) {
      case 'Sin empezar': return 'secondary';
      case 'En progreso': return 'warning';
      case 'Listo': return 'success';
      default: return 'light';
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3">Cargando datos del afiliado...</p>
        </div>
      </div>
    );
  }

  if (!afiliado) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Afiliado no encontrado</h4>
          <p>No se pudo encontrar el afiliado con ID: {id}</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin-afiliado')}>
            Regresar a lista de afiliados
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container py-5">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Administración de Afiliados', path: '/admin-afiliado' },
            { label: afiliado.nombre }
          ]}
        />
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>
            <i className="fas fa-user-tie me-2 text-primary"></i>
            {afiliado.nombre}
            {(afiliado.mls_agent_key || afiliado.creado_auto) && (
              <span className="badge bg-info ms-2" style={{ fontSize: '0.5em' }}>MLS</span>
            )}
          </h2>
          {afiliado.verificacion_pendiente && (
            <button
              className="btn btn-success"
              onClick={async () => {
                await updateDoc(doc(db, 'asesor', id!), { verificacion_pendiente: false });
                setAfiliado((prev: any) => ({ ...prev, verificacion_pendiente: false }));
              }}
            >
              <i className="fas fa-check-circle me-2"></i>Marcar como verificado
            </button>
          )}
        </div>

        {afiliado.verificacion_pendiente && (
          <div className="alert alert-warning d-flex align-items-center mb-4">
            <i className="fas fa-exclamation-triangle me-3 fa-lg"></i>
            <div>
              <strong>Afiliado pendiente de verificacion.</strong> Fue creado automaticamente por el sync MLS.
              Revisa los datos y haz clic en "Marcar como verificado" si es correcto.
              {afiliado.mls_agent_key && (
                <span className="d-block mt-1"><small className="text-muted">MLS Agent Key: <code>{afiliado.mls_agent_key}</code></small></span>
              )}
            </div>
          </div>
        )}

        {afiliado.creado_auto && !afiliado.verificacion_pendiente && (
          <div className="alert alert-info py-2 mb-4" style={{ fontSize: '0.85rem' }}>
            <i className="fas fa-database me-2"></i>
            Afiliado vinculado al MLS. Sus propiedades se sincronizan automaticamente.
          </div>
        )}

        <div className="row">
          <div className="col-md-4">
            <div className="card">
              <div className="card-header">
                <h5><i className="fas fa-user me-2"></i>Perfil</h5>
              </div>
              <div className="card-body text-center">
                {afiliado.foto ? (
                  <img
                    src={afiliado.foto}
                    alt={afiliado.nombre}
                    className="img-fluid rounded-circle mb-3"
                    style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    className="bg-secondary rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                    style={{ width: '150px', height: '150px' }}
                  >
                    <i className="fas fa-user fa-4x text-white"></i>
                  </div>
                )}
                <h5>{afiliado.nombre}</h5>
                <p className="text-muted">{afiliado.especialidad || 'Asesor Inmobiliario'}</p>

                {/* Redes Sociales */}
                {(afiliado.redes?.facebook || afiliado.redes?.instagram || afiliado.sitio_web) && (
                  <div className="mt-3">
                    <h6>Redes Sociales</h6>
                    <div className="d-flex justify-content-center gap-2">
                      {afiliado.redes?.facebook && (
                        <a href={afiliado.redes.facebook} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                          <i className="fab fa-facebook"></i>
                        </a>
                      )}
                      {afiliado.redes?.instagram && (
                        <a href={afiliado.redes.instagram} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-danger">
                          <i className="fab fa-instagram"></i>
                        </a>
                      )}
                      {afiliado.sitio_web && (
                        <a href={afiliado.sitio_web} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary">
                          <i className="fas fa-globe"></i>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card">
              <div className="card-header">
                <h5><i className="fas fa-info-circle me-2"></i>Información de Contacto</h5>
              </div>
              <div className="card-body">
                <p><strong><i className="fas fa-envelope me-2 text-primary"></i>Correo:</strong><br/>{afiliado.correo || 'No especificado'}</p>
                <p><strong><i className="fas fa-phone me-2 text-success"></i>Teléfono:</strong><br/>{afiliado.telefono || 'No especificado'}</p>
                <div className="mb-3">
                  <strong><i className="fas fa-building me-2 text-info"></i>Agencia:</strong>
                  <div className="d-flex align-items-center gap-2 mt-1">
                    {inmobiliariaId ? (
                      <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/ver-inm/${inmobiliariaId}`)}>
                        {inmobiliariaNombre}
                      </button>
                    ) : (
                      <span className="text-danger">Sin asignar</span>
                    )}
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={async () => {
                        if (!mostrarCambiarAgencia) {
                          const snap = await getDocs(collection(db, 'inmobiliaria'));
                          const agencias = snap.docs
                            .map(d => ({ id: d.id, nombre: d.data().nombre || 'Sin nombre', verificacion_pendiente: d.data().verificacion_pendiente }))
                            .filter(a => !a.verificacion_pendiente)
                            .sort((a, b) => a.nombre.localeCompare(b.nombre));
                          setAgenciasDisponibles(agencias);
                        }
                        setMostrarCambiarAgencia(!mostrarCambiarAgencia);
                        setBusquedaAgencia('');
                      }}
                      title="Cambiar agencia"
                    >
                      <i className={`fas ${mostrarCambiarAgencia ? 'fa-times' : 'fa-exchange-alt'}`}></i>
                    </button>
                  </div>
                  {mostrarCambiarAgencia && (
                    <div className="mt-2 border rounded p-2">
                      <input
                        type="text"
                        className="form-control form-control-sm mb-2"
                        placeholder="Buscar agencia..."
                        value={busquedaAgencia}
                        onChange={e => setBusquedaAgencia(e.target.value)}
                      />
                      <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {agenciasDisponibles
                          .filter(a => !busquedaAgencia || a.nombre.toLowerCase().includes(busquedaAgencia.toLowerCase()))
                          .slice(0, 15)
                          .map(a => (
                            <div
                              key={a.id}
                              className={`d-flex justify-content-between align-items-center py-1 border-bottom ${a.id === inmobiliariaId ? 'bg-light' : ''}`}
                            >
                              <small className={a.id === inmobiliariaId ? 'fw-bold' : ''}>{a.nombre}</small>
                              {a.id !== inmobiliariaId && (
                                <button
                                  className="btn btn-sm btn-success py-0"
                                  onClick={async () => {
                                    try {
                                      // Quitar de agencia anterior
                                      if (inmobiliariaId) {
                                        await updateDoc(doc(db, 'inmobiliaria', inmobiliariaId), {
                                          asesor_id: arrayRemove(id)
                                        });
                                      }
                                      // Agregar a nueva agencia
                                      await updateDoc(doc(db, 'inmobiliaria', a.id), {
                                        asesor_id: arrayUnion(id)
                                      });
                                      // Actualizar asesor
                                      await updateDoc(doc(db, 'asesor', id!), {
                                        inmobiliaria_doc_ids: [a.id]
                                      });
                                      setInmobiliariaNombre(a.nombre);
                                      setInmobiliariaId(a.id);
                                      setMostrarCambiarAgencia(false);
                                      Swal.fire({ icon: 'success', title: 'Agencia asignada', text: `Ahora pertenece a ${a.nombre}`, timer: 1500, showConfirmButton: false });
                                    } catch (err) {
                                      console.error('Error asignando agencia:', err);
                                      Swal.fire('Error', 'No se pudo asignar la agencia.', 'error');
                                    }
                                  }}
                                >
                                  <i className="fas fa-check"></i>
                                </button>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
                <p><strong><i className="fas fa-briefcase me-2 text-warning"></i>Experiencia:</strong><br/>{afiliado.anos_experiencia || 'No especificado'}</p>
                {afiliado.biografia && (
                  <div className="mt-3">
                    <strong><i className="fas fa-user-tie me-2 text-secondary"></i>Biografía:</strong>
                    <p className="text-muted mt-2">{afiliado.biografia}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card">
              <div className="card-header">
                <h5><i className="fas fa-chart-bar me-2"></i>Estadísticas</h5>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <span><i className="fas fa-star text-warning me-2"></i><strong>Calificación:</strong></span>
                  <span className="badge bg-info fs-6">{promedioCalificacion !== 'N/A' ? `${promedioCalificacion}/5` : promedioCalificacion}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <span><i className="fas fa-comment-dots text-primary me-2"></i><strong>Reseñas:</strong></span>
                  <span className="badge bg-primary fs-6">{reseñas.length}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <span><i className="fas fa-graduation-cap text-success me-2"></i><strong>Talleres Asistidos:</strong></span>
                  <span className="badge bg-success fs-6">{talleresCompletados}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span><i className="fas fa-award text-danger me-2"></i><strong>Créditos:</strong></span>
                  <span className="badge bg-danger fs-6">{creditos}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5><i className="fas fa-home me-2"></i>Propiedades MLS Vinculadas ({propiedades.length})</h5>
              </div>
              <div className="card-body">
                <span className="badge bg-primary">{propiedades.length} propiedades MLS activas</span>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Propiedades */}
        {propiedades.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0"><i className="fas fa-list me-2"></i>Propiedades MLS</h5>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => navigate(`/admin-property?asesorUid=${afiliado.id}`)}
                  >
                    <i className="fas fa-external-link-alt me-1"></i>
                    Ver en listado completo
                  </button>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table aria-label="Tabla de Ver Admin Afiliado" className="table table-sm table-hover">
                      <thead>
                        <tr>
                          <th scope="col">Propiedad</th>
                          <th scope="col">Tipo</th>
                          <th scope="col">Zona</th>
                          <th scope="col">Precio</th>
                          <th scope="col"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {propiedades.slice(0, 15).map((prop: any) => (
                          <tr key={prop.id}>
                            <td>
                              <strong style={{ fontSize: '0.9rem' }}>{prop.titulo || prop.nombre || 'Sin titulo'}</strong>
                              <br />
                              <span className="badge bg-primary me-1" style={{ fontSize: '0.65rem' }}>MLS</span>
                              {prop.datos_flex?.rets_class && (
                                <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>{prop.datos_flex.rets_class}</span>
                              )}
                            </td>
                            <td><small>{prop.tipo_propiedad || '-'}</small></td>
                            <td><small>{prop.zona || prop.direccion?.area || '-'}</small></td>
                            <td>
                              <span className="text-success fw-bold" style={{ fontSize: '0.9rem' }}>
                                {prop.precio_actual_usd ? `$${prop.precio_actual_usd.toLocaleString()} USD` : 'N/A'}
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => navigate(`/view-admin-property/${prop.id}`)}
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {propiedades.length > 15 && (
                      <div className="text-center mt-2">
                        <button
                          className="btn btn-link btn-sm"
                          onClick={() => navigate(`/admin-property?asesorUid=${afiliado.id}`)}
                        >
                          Ver las {propiedades.length} propiedades
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Talleres y Capacitaciones */}
        {talleres.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5><i className="fas fa-graduation-cap me-2"></i>Talleres y Capacitaciones</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table aria-label="Tabla de Ver Admin Afiliado" className="table table-striped">
                      <thead>
                        <tr>
                          <th scope="col">Taller</th>
                          <th scope="col">Estatus</th>
                          <th scope="col">Créditos</th>
                          <th scope="col">Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {talleres.map((taller: any, index: number) => (
                          <tr key={index}>
                            <td>{taller.nombre || 'Sin nombre'}</td>
                            <td>
                              {getBadge(taller.estatus, getColorEstatus(taller.estatus))}
                            </td>
                            <td>{taller.creditos || 0}</td>
                            <td>
                              {taller.fecha?.toDate?.().toLocaleDateString('es-MX') ||
                               taller.fechaInicio?.toDate?.().toLocaleDateString('es-MX') ||
                               'No disponible'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reseñas */}
        {reseñas.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5><i className="fas fa-star me-2"></i>Reseñas Aprobadas</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {reseñas.slice(0, 6).map((reseña: any, index: number) => (
                      <div key={index} className="col-md-4 mb-3">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>{reseña.cliente_nombre || 'Cliente'}</strong>
                              <div>
                                {[1, 2, 3, 4, 5].map(star => (
                                  <i
                                    key={star}
                                    className={`fas fa-star ${star <= (reseña.estrellas || 0) ? 'text-warning' : 'text-muted'}`}
                                  ></i>
                                ))}
                              </div>
                            </div>
                            <p className="mb-0">{reseña.comentario || 'Sin comentario'}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Acciones */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5><i className="fas fa-cogs me-2"></i>Acciones Administrativas</h5>
              </div>
              <div className="card-body">
                <div className="btn-group" role="group">
                  <Link 
                    to={`/agent-page/${afiliado.id}`} 
                    className="btn btn-outline-info"
                  >
                    <i className="fas fa-eye me-2"></i>
                    Ver Perfil Público
                  </Link>
                  <button
                    className="btn btn-outline-warning"
                    onClick={() => navigate(`/editar-afiliado/${afiliado.id}`)}
                  >
                    <i className="fas fa-edit me-2"></i>
                    Editar Perfil
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => navigate(`/admin-property?asesorUid=${afiliado.id}`)}
                  >
                    <i className="fas fa-home me-2"></i>
                    Gestionar Propiedades ({propiedades.length})
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/admin-afiliado')}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Volver a Lista
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}